import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api/client';
/**
 * Query keys for employee data.
 * Centralized to avoid typos and enable easy invalidation.
 */
export const employeeKeys = {
    all: ['employees'],
    lists: () => [...employeeKeys.all, 'list'],
    list: (filters) => [...employeeKeys.lists(), filters],
    details: () => [...employeeKeys.all, 'detail'],
    detail: (id) => [...employeeKeys.details(), id],
    wageHistory: (id) => [...employeeKeys.detail(id), 'wages'],
};
/**
 * Fetch list of employees.
 * Returns all employees for the authenticated user's tenant.
 */
export function useEmployees() {
    return useQuery({
        queryKey: employeeKeys.lists(),
        queryFn: async () => {
            const response = await apiClient.get('/employees');
            return response.employees;
        },
    });
}
/**
 * Fetch a single employee by ID.
 */
export function useEmployee(employeeId) {
    return useQuery({
        queryKey: employeeKeys.detail(employeeId),
        queryFn: async () => {
            // Backend returns employee object directly, not wrapped
            const employee = await apiClient.get(`/employees/${employeeId}`);
            return employee;
        },
        enabled: Boolean(employeeId),
    });
}
/**
 * Fetch wage history for an employee.
 */
export function useWageHistory(employeeId) {
    return useQuery({
        queryKey: employeeKeys.wageHistory(employeeId),
        queryFn: async () => {
            const response = await apiClient.get(`/employees/${employeeId}/wage-history`);
            return response;
        },
        enabled: Boolean(employeeId),
    });
}
/**
 * Create a new employee.
 * Includes optimistic update and automatic cache invalidation.
 */
export function useCreateEmployee() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            // Backend returns employee object directly, not wrapped
            const newEmployee = await apiClient.post('/employees', data);
            return newEmployee;
        },
        onSuccess: (newEmployee) => {
            // Invalidate employees list to refetch
            queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
            // Optionally add the new employee to the cache
            queryClient.setQueryData(employeeKeys.detail(newEmployee.employeeId), newEmployee);
        },
    });
}
/**
 * Update an existing employee.
 * Includes optimistic update and automatic cache invalidation.
 */
export function useUpdateEmployee(employeeId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            // Backend returns employee object directly, not wrapped
            const updatedEmployee = await apiClient.put(`/employees/${employeeId}`, data);
            return updatedEmployee;
        },
        onMutate: async (updatedData) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: employeeKeys.detail(employeeId),
            });
            // Snapshot the previous value
            const previousEmployee = queryClient.getQueryData(employeeKeys.detail(employeeId));
            // Optimistically update to the new value
            if (previousEmployee) {
                queryClient.setQueryData(employeeKeys.detail(employeeId), {
                    ...previousEmployee,
                    ...updatedData,
                    updatedAt: new Date().toISOString(),
                });
            }
            return { previousEmployee };
        },
        onError: (_err, _variables, context) => {
            // Rollback to previous value on error
            if (context?.previousEmployee) {
                queryClient.setQueryData(employeeKeys.detail(employeeId), context.previousEmployee);
            }
        },
        onSuccess: (updatedEmployee) => {
            // Update the cache with server response
            queryClient.setQueryData(employeeKeys.detail(employeeId), updatedEmployee);
            // Invalidate list to refetch (in case name/position changed)
            queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
        },
    });
}
/**
 * Archive (soft delete) an employee by setting status to 'terminated' and adding endDate.
 * This doesn't delete the employee from the database, just marks them as archived.
 */
export function useArchiveEmployee(employeeId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
            const updatedEmployee = await apiClient.put(`/employees/${employeeId}`, {
                status: 'terminated',
                endDate: today,
            });
            return updatedEmployee;
        },
        onSuccess: () => {
            // Invalidate all queries to refetch
            queryClient.invalidateQueries({ queryKey: employeeKeys.all });
        },
    });
}
/**
 * Reactivate an archived employee by setting status to 'active' and removing endDate.
 */
export function useReactivateEmployee(employeeId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const updatedEmployee = await apiClient.put(`/employees/${employeeId}`, {
                status: 'active',
                endDate: '',
            });
            return updatedEmployee;
        },
        onSuccess: () => {
            // Invalidate all queries to refetch
            queryClient.invalidateQueries({ queryKey: employeeKeys.all });
        },
    });
}
/**
 * Record a wage change for an employee.
 */
export function useRecordWageChange(employeeId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            const response = await apiClient.post(`/employees/${employeeId}/wage-change`, data);
            return response.wageChange;
        },
        onSuccess: () => {
            // Invalidate wage history
            queryClient.invalidateQueries({
                queryKey: employeeKeys.wageHistory(employeeId),
            });
            // Invalidate employee detail (to refresh currentWage)
            queryClient.invalidateQueries({
                queryKey: employeeKeys.detail(employeeId),
            });
        },
    });
}
/**
 * Delete an employee (soft delete by setting status to 'terminated').
 */
export function useDeleteEmployee(employeeId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            await apiClient.delete(`/employees/${employeeId}`);
        },
        onSuccess: () => {
            // Invalidate lists
            queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
            // Remove from cache
            queryClient.removeQueries({ queryKey: employeeKeys.detail(employeeId) });
        },
    });
}
/**
 * Update account status for an employee.
 * Admin-only operation to change Cognito account state tracking.
 */
export function useUpdateAccountStatus(employeeId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (accountStatus) => {
            const updatedEmployee = await apiClient.put(`/employees/${employeeId}/account-status`, { accountStatus });
            return updatedEmployee;
        },
        onSuccess: (updatedEmployee) => {
            // Update the cache with server response
            queryClient.setQueryData(employeeKeys.detail(employeeId), updatedEmployee);
            // Invalidate list to refetch
            queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
        },
    });
}
